import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, ExternalLink, Video, Clock, CheckCircle, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CritiqueHistoryPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [critiques, setCritiques] = useState([]);
  const [selectedCritique, setSelectedCritique] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCritiques();
  }, []);

  useEffect(() => {
    if (id && critiques.length > 0) {
      const found = critiques.find(c => c.id === id);
      setSelectedCritique(found);
    }
  }, [id, critiques]);

  const fetchCritiques = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/critiques`);
      setCritiques(response.data.critiques || []);
    } catch (error) {
      console.error('Failed to fetch critiques:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Detail View
  if (selectedCritique) {
    return (
      <div className="min-h-screen bg-background pb-24 md:pb-8">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Link to="/critiques" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to All Critiques
          </Link>

          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <Badge 
                variant={selectedCritique.status === 'complete' || selectedCritique.status === 'completed' ? 'default' : 'secondary'}
                className={selectedCritique.status === 'complete' || selectedCritique.status === 'completed' ? 'bg-secondary' : 'bg-warning text-foreground'}
              >
                {selectedCritique.status === 'complete' || selectedCritique.status === 'completed' ? 'Completed' : 'Pending'}
              </Badge>
              {selectedCritique.turnaround === 'priority' && (
                <Badge variant="outline" className="badge-priority">Priority</Badge>
              )}
            </div>
            <h1 className="font-display font-bold text-2xl md:text-3xl uppercase tracking-tight">
              {selectedCritique.discipline} Critique
            </h1>
            <p className="text-muted-foreground mt-1">Submitted {formatDate(selectedCritique.created_at)}</p>
          </div>

          {/* Original Request */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-display text-lg uppercase flex items-center gap-2">
                <Video className="w-5 h-5" />
                Your Submission
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedCritique.video_link ? (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Video Link</p>
                  <a 
                    href={selectedCritique.video_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-2"
                  >
                    {selectedCritique.video_link}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              ) : (
                <p className="text-muted-foreground italic">Video shared directly with coach</p>
              )}
              
              {selectedCritique.note && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Your Note</p>
                  <p className="bg-muted/50 p-4 rounded-lg">{selectedCritique.note}</p>
                </div>
              )}
              
              <div className="flex gap-4 text-sm text-muted-foreground">
                <span>Elevated Credits used: {selectedCritique.credits_used}</span>
                <span>•</span>
                <span>{selectedCritique.sharing === 'anonymized' ? 'Shared anonymously' : 'Private'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Coach Response */}
          <Card className={selectedCritique.response || selectedCritique.response_content ? 'coach-response' : ''}>
            <CardHeader>
              <CardTitle className="font-display text-lg uppercase flex items-center gap-2">
                <User className="w-5 h-5" />
                Coach Response
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedCritique.status === 'pending' ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-warning mx-auto mb-4" />
                  <p className="font-semibold">Waiting for Coach</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedCritique.turnaround === 'priority' 
                      ? 'Your request is at the front of the queue.' 
                      : 'Your request is being handled in order received.'}
                  </p>
                </div>
              ) : selectedCritique.response ? (
                // Old response format (response object)
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge className="badge-coach">Coach</Badge>
                    <span className="font-semibold">{selectedCritique.response.coach_name}</span>
                    <span className="text-muted-foreground text-sm">
                      • {formatDate(selectedCritique.response.created_at)}
                    </span>
                  </div>
                  
                  {selectedCritique.response.type === 'text' ? (
                    <div className="bg-primary/5 p-4 rounded-lg">
                      <p className="whitespace-pre-wrap">{selectedCritique.response.content}</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {selectedCritique.response.type === 'video_link' ? 'Video Response' : 'Voice Response'}
                      </p>
                      <a 
                        href={selectedCritique.response.content} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-2"
                      >
                        {selectedCritique.response.content}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                </div>
              ) : selectedCritique.response_content ? (
                // New response format (response_type, response_content fields)
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge className="badge-coach">Coach</Badge>
                    <span className="font-semibold">{selectedCritique.coach_name}</span>
                    {selectedCritique.responded_at && (
                      <span className="text-muted-foreground text-sm">
                        • {formatDate(selectedCritique.responded_at)}
                      </span>
                    )}
                  </div>
                  
                  {selectedCritique.response_type === 'text' ? (
                    <div className="bg-primary/5 p-4 rounded-lg">
                      <p className="whitespace-pre-wrap">{selectedCritique.response_content}</p>
                    </div>
                  ) : selectedCritique.response_type === 'video_studio' ? (
                    <div className="bg-primary/5 p-4 rounded-lg space-y-4">
                      <div>
                        <p className="font-medium mb-1">Video Studio Response</p>
                        <p className="text-muted-foreground text-sm">
                          Your coach has created an annotated video response with visual highlights and commentary.
                        </p>
                      </div>
                      {selectedCritique.annotated_video_id && (
                        <Link to={`/video-response/${selectedCritique.annotated_video_id}`}>
                          <Button className="w-full sm:w-auto">
                            <Video className="w-4 h-4 mr-2" />
                            Watch Video Response
                          </Button>
                        </Link>
                      )}
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {selectedCritique.response_type === 'video_link' ? 'Video Response' : 
                         selectedCritique.response_type === 'video_upload' ? 'Video Response' : 'Voice Response'}
                      </p>
                      <a 
                        href={selectedCritique.response_content} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-2 break-all"
                      >
                        {selectedCritique.response_content}
                        <ExternalLink className="w-4 h-4 flex-shrink-0" />
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-secondary mx-auto mb-4" />
                  <p className="font-semibold">Response Complete</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your coach has completed this critique.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Link to="/dashboard" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight">
            Critique History
          </h1>
          <p className="text-muted-foreground mt-2">All your critique requests and coach responses.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : critiques.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No critiques yet</p>
              <Link to="/critique/new">
                <Button className="mt-4 rounded-full">Submit Your First Critique</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {critiques.map(critique => (
              <Link key={critique.id} to={`/critiques/${critique.id}`}>
                <Card className="critique-card hover:bg-muted/30 transition-colors cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge 
                            variant={critique.status === 'complete' || critique.status === 'completed' ? 'default' : 'secondary'}
                            className={critique.status === 'complete' || critique.status === 'completed' ? 'bg-secondary' : 'bg-warning text-foreground'}
                          >
                            {critique.status === 'complete' || critique.status === 'completed' ? (
                              <><CheckCircle className="w-3 h-3 mr-1" /> Completed</>
                            ) : (
                              <><Clock className="w-3 h-3 mr-1" /> Pending</>
                            )}
                          </Badge>
                          {critique.turnaround === 'priority' && (
                            <Badge variant="outline" className="badge-priority">Priority</Badge>
                          )}
                        </div>
                        <h3 className="font-display font-bold text-lg uppercase">{critique.discipline}</h3>
                        {critique.note && (
                          <p className="text-muted-foreground text-sm mt-1 line-clamp-2">{critique.note}</p>
                        )}
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        <p>{new Date(critique.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</p>
                        <p className="text-xs">{critique.credits_used} credit{critique.credits_used > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CritiqueHistoryPage;
